import './DiscoverTab.scss';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CreatorFilterState } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import {
  GetCreatorInsightsResponse,
  YouTubeCreatorSetWithMembers
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { AudienceInsightsDimensions } from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { debounceAsync } from '@src/utils/async';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  NO_AUDIENCE_INSIGHT_MATCHES,
  searchAudienceInsights
} from '../hooks/use-audience-insights';
import { SET_PARAM } from '../CreatorSets/creator-sets-constants';
import { PAGE_TITLE, Tab } from '../youtube-planning-constants';
import { AudienceInsightMenuItem, GoogleAdsAccount } from '../youtube-planning-types';
import { CreatorFilterPanel } from './CreatorFilterPanel';
import { CreatorResultsTable } from './CreatorResultsTable';
import { SaveCreatorSetModal } from './SaveCreatorSetModal';
import { TYPEAHEAD_DEBOUNCE_MS, URL_PARAMS } from './creator-discovery-constants';
import {
  describeCreatorResults,
  isSearchable,
  paramsToSearch,
  searchToParams,
  toCreatorRows
} from './creator-discovery-utils';
import { useCreatorInsights } from './hooks/use-creator-insights';

interface DiscoverTabProps {
  adsAccount?: GoogleAdsAccount;
  // getGoogleAdsAccount failed, so there's nothing to search with.
  noAdsAccount: boolean;
}

export const DiscoverTab = ({ adsAccount, noAdsAccount }: DiscoverTabProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  // The submitted search lives in the URL, so a search can be shared or bookmarked.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get(URL_PARAMS.SEARCH) ?? '';
  const { search, labels } = useMemo(() => paramsToSearch(searchParams), [searchParams]);

  const { data, isFetching, isError, error, refetch } = useCreatorInsights(cid, adsAccount, search);
  // A search from the URL can only run once the Ads account has loaded; until then it's pending,
  // not "no matches".
  const hasSearched = Boolean(search && isSearchable(search));
  const waitingForAccount = hasSearched && !adsAccount && !noAdsAccount;

  useEffect(() => {
    if (error) {
      // The lambda's 429/403/400 messages are written for the user, so show them as-is.
      setError({ title: "Couldn't search creators", message: error.message, reportError: error });
    }
  }, [error, setError]);

  // Failures surface, so a 403, a Google outage and a genuine no-match don't all look like an
  // empty menu.
  const fetchAudienceOptions = useMemo(
    () =>
      debounceAsync(
        async (
          input: string,
          dimensions: AudienceInsightsDimensions[]
        ): Promise<AudienceInsightMenuItem[]> => {
          if (!adsAccount || !input.trim()) return [];
          try {
            return await searchAudienceInsights(cid, adsAccount, input, dimensions);
          } catch (e) {
            if (e.message === NO_AUDIENCE_INSIGHT_MATCHES) return [];
            setError({
              title: "Couldn't search Google audiences",
              message: e.message,
              reportError: e
            });
            return [];
          }
        },
        TYPEAHEAD_DEBOUNCE_MS
      ),
    [cid, adsAccount, setError]
  );

  return (
    <div className='discover-tab'>
      {noAdsAccount && (
        <div className='discover-no-ads-account' role='status'>
          This company has no Google Ads account connected, so creator search isn&apos;t available.
        </div>
      )}
      <CreatorFilterPanel
        // Remount on a new URL search (including back/forward) so the inputs show it.
        key={searchParam}
        initialSearch={search}
        initialLabels={labels}
        fetchAudienceOptions={fetchAudienceOptions}
        disabled={!adsAccount}
        searching={isFetching}
        onSearch={(nextSearch, nextLabels) => {
          const nextParams = searchToParams(nextSearch, nextLabels);
          const sameSearch = nextParams[URL_PARAMS.SEARCH] === searchParam;
          // The same search again only re-runs after a failure; otherwise its result is already
          // on screen and repeating the billed call would change nothing.
          if (sameSearch && !isError) return;
          if (sameSearch) {
            refetch();
          } else {
            setSearchParams(nextParams);
          }
          Mixpanel.track(MxE.ActionTriggered, { page: PAGE_TITLE, action: 'search_creators' });
        }}
      />
      <CreatorResults
        // A new search starts with nothing selected. Prefixed so it can't collide with the
        // filter panel's key.
        key={`results-${searchParam}`}
        search={search}
        adsAccount={adsAccount}
        hasSearched={hasSearched}
        isLoading={isFetching || waitingForAccount}
        searchUnavailable={noAdsAccount}
        error={error}
        data={data}
      />
    </div>
  );
};

interface CreatorResultsProps {
  search?: CreatorFilterState;
  adsAccount?: GoogleAdsAccount;
  hasSearched: boolean;
  isLoading: boolean;
  searchUnavailable: boolean;
  error: Error | null;
  data?: GetCreatorInsightsResponse;
}

interface SavedNote {
  set: YouTubeCreatorSetWithMembers;
  added: number;
}

const CreatorResults = ({
  search,
  adsAccount,
  hasSearched,
  isLoading,
  searchUnavailable,
  error,
  data
}: CreatorResultsProps) => {
  const { cid } = useCompanyInfo();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<SavedNote>();
  const rows = useMemo(() => (data ? toCreatorRows(data) : []), [data]);

  // Selection order is the order members are saved in.
  const selectedChannels = useMemo(() => {
    const channelsById = new Map(rows.map(row => [row.channelId, row.channel]));
    return [...selected].flatMap(id => {
      const channel = channelsById.get(id);
      return channel ? [channel] : [];
    });
  }, [rows, selected]);

  return (
    <div className='creator-results' data-testid='creator-results'>
      <div className='creator-results-header'>
        <div className='header'>Creators</div>
        {data && rows.length > 0 && !isLoading && (
          <div className='creator-results-summary'>{describeCreatorResults(data, rows)}</div>
        )}
        {savedNote && (
          <div className='creator-results-saved' role='status'>
            Added {conditionallyPluralizeWithCount(savedNote.added, 'creator')} to &ldquo;
            {savedNote.set.name}&rdquo;.{' '}
            <Link to={`/${cid}/youtube/planning/${Tab.SETS}?${SET_PARAM}=${savedNote.set.id}`}>
              Open set
            </Link>
          </div>
        )}
      </div>
      <CreatorResultsTable
        // BPMTable reports its filtered rows only when the filter changes, so it remounts once
        // the results arrive rather than keep the empty list it saw while loading.
        key={isLoading ? 'loading' : 'loaded'}
        rows={isLoading ? [] : rows}
        hasSearched={hasSearched}
        isLoading={isLoading}
        searchUnavailable={searchUnavailable}
        error={error}
        selected={selected}
        onSelectedChange={setSelected}
        selectionActions={
          <Button size={Size.SM} disabled={!adsAccount} onClick={() => setSaving(true)}>
            Save to set
          </Button>
        }
      />
      {saving && search && adsAccount && (
        <SaveCreatorSetModal
          creators={selectedChannels}
          search={search}
          adsAccount={adsAccount}
          onHide={() => setSaving(false)}
          onSaved={(set, added) => {
            setSaving(false);
            setSelected(new Set());
            setSavedNote({ set, added });
          }}
        />
      )}
    </div>
  );
};
